import { MailCheck } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { apiRequest } from '../../api/client'
import { BrandLockup } from '../../components/Brand'
import { useAuth, type AuthUser } from './authContext'

// How often the confirmation page checks whether the link was opened elsewhere.
const VERIFIED_POLL_MS = 5_000

/**
 * Shown in place of a workspace screen the account's plan leaves out: the
 * email confirmation page for an unconfirmed account, otherwise a short note.
 */
export function PlanGate({ user }: { user: AuthUser }) {
  if (user.plan?.id === 'unverified') return <VerifyEmailGate user={user} />
  return (
    <main className="auth-page">
      <div className="auth-card card">
        <BrandLockup className="auth-brand" showTagline />
        <h1>Not included in your plan</h1>
        <p>This screen is not part of your plan ({user.plan?.label ?? 'unknown'}).</p>
        <Link className="button button--primary button--full" to="/overview">Back to the overview</Link>
      </div>
    </main>
  )
}

function VerifyEmailGate({ user }: { user: AuthUser }) {
  const auth = useAuth()
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(!user.email)
  const [email, setEmail] = useState(user.email ?? '')
  const { refreshUser } = auth

  // Opening the link in another tab or on a phone lets this tab in on its own.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refreshUser()
    }, VERIFIED_POLL_MS)
    return () => clearInterval(timer)
  }, [refreshUser])

  const run = async (action: () => Promise<string>) => {
    setBusy(true)
    setNotice(null)
    try {
      setNotice({ kind: 'ok', text: await action() })
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(false)
    }
  }

  const resend = () => run(async () => {
    const sent = await apiRequest<{ sent_to: string }>('/api/auth/verify-email/resend', { method: 'POST' })
    return `A new link is on its way to ${sent.sent_to}.`
  })

  const checkAgain = () => run(async () => {
    const fresh = await refreshUser()
    if (fresh && fresh.plan?.id !== 'unverified') return 'Confirmed.'
    throw new Error('Not confirmed yet. Open the link in the email, then try again.')
  })

  const changeAddress = (event: FormEvent) => {
    event.preventDefault()
    void run(async () => {
      await apiRequest('/api/auth/me', { method: 'PATCH', body: JSON.stringify({ email: email.trim() }) })
      await refreshUser()
      setEditing(false)
      return `Saved. A confirmation link is on its way to ${email.trim()}.`
    })
  }

  return (
    <main className="auth-page">
      <div className="auth-card card" aria-labelledby="verify-email-title">
        <BrandLockup className="auth-brand" showTagline />
        <MailCheck className="auth-card__icon" aria-hidden="true" />
        <h1 id="verify-email-title">Confirm your email address</h1>
        {user.email ? (
          <p>
            We sent a confirmation link to <strong>{user.email}</strong>. Open it to start using Shade Research;
            it works for 48 hours. Check your spam folder if it has not arrived within a few minutes.
          </p>
        ) : (
          <p>Add your email address to finish setting up your account. We will send a link to confirm it.</p>
        )}

        {notice && (
          <div className={notice.kind === 'ok' ? 'callout callout--success' : 'callout callout--warning'} role={notice.kind === 'ok' ? 'status' : 'alert'}>
            {notice.text}
          </div>
        )}

        {editing ? (
          <form className="auth-form stack" onSubmit={changeAddress} aria-label="Change email address">
            <label className="field-label">
              Email address
              <input className="input" type="email" name="email" autoComplete="email" value={email}
                onChange={event => setEmail(event.target.value)} required />
            </label>
            <button className="button button--primary button--full" type="submit" disabled={busy || !email.trim()}>
              {busy ? 'Please wait…' : 'Send the confirmation link'}
            </button>
            {user.email && <button className="text-button" type="button" onClick={() => setEditing(false)}>Cancel</button>}
          </form>
        ) : (
          <div className="stack">
            <button className="button button--primary button--full" type="button" onClick={() => void checkAgain()} disabled={busy}>
              I have confirmed it
            </button>
            <button className="button button--secondary button--full" type="button" onClick={() => void resend()} disabled={busy}>
              Send the link again
            </button>
          </div>
        )}

        <div className="auth-footer">
          {!editing && <button className="text-button" type="button" onClick={() => setEditing(true)}>Wrong address? Change it</button>}
          <button className="text-button" type="button" onClick={() => void auth.logout()}>Sign out</button>
        </div>
      </div>
    </main>
  )
}
